import React, { useEffect, useRef, useState } from 'react';
import { createC2PSession, confirmClickToPay } from '../../api';
import '../checkOutPage/clickToPay/ClickToPayCheckout.css';

const loadScript = (src) =>
  new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const sc = document.createElement('script');
    sc.src = src;
    sc.async = false;
    sc.onload = () => resolve();
    sc.onerror = () => reject(new Error('Failed to load ' + src));
    document.body.appendChild(sc);
  });

const show = (id) => {
  const el = document.getElementById(id);
  if (el) el.style.display = '';
};
const hide = (id) => {
  const el = document.getElementById(id);
  if (el) el.style.display = 'none';
};

const maskEmail = (email) => {
  if (!email) return '';
  const [local, domain] = email.split('@');
  if (!domain) return email;
  if (local.length <= 2) return '*'.repeat(local.length) + '@' + domain;
  return local[0] + '*****' + local[local.length - 1] + '@' + domain;
};

const isElementVisible = (el) =>
  el &&
  el.offsetParent !== null &&
  getComputedStyle(el).display !== 'none' &&
  getComputedStyle(el).visibility !== 'hidden';

const checkC2PConsent = () => {
  for (const containerId of [
    'consent-containerMaster',
    'consent-containerVisa'
  ]) {
    const container = document.getElementById(containerId);
    const consent = container && container.querySelector('src-consent');
    if (consent && consent.shadowRoot) {
      const checkbox = consent.shadowRoot.querySelector(
        'input[name="consent-checkbox"]'
      );
      if (isElementVisible(container) && checkbox && checkbox.checked)
        return true;
    }
  }
  return false;
};

// Mobile hand-off variant of ClickToPayCheckout. The mobile app already owns
// order creation (its own init_pay/init_pay_all path mints the mco) and
// passes an EXISTING `mco` + `amount` in — this widget never mints an mco of
// its own. It only does two things: (1) create/configure the Click to Pay
// session and let the shopper pick a card, (2) call confirm_c2p_web with the
// mco it was given to finalize the charge. There is no init_pay_web call
// here — confirm_c2p_web's result is the ONLY source of truth for
// success/failure, so its status must be checked strictly (unlike the web
// modal, which has init_pay_web as a first authorization step).
const MobileClickToPayWidget = ({ mco, amount, onSuccess, onError, onClose }) => {
  const [phase, setPhase] = useState('loading'); // loading | ready | paying | done | error
  const [errorMsg, setErrorMsg] = useState('');
  const sess = useRef({ sessionId: '' });
  const flags = useRef({
    inited: false,
    doEnroll: false,
    emailLookupInitiated: false,
    addingNewCard: false,
    settled: false,
    psConfigured: false,
    cardFormReady: false
  });

  useEffect(() => {
    if (flags.current.inited) return;
    flags.current.inited = true;
    let cancelled = false;

    const hidePageContent = () => {
      hide('c2p-loader');
      hide('clickToPaySignIn');
      hide('guestCheckoutPanel');
    };
    const showPageContent = () => {
      hide('c2p-popup');
      hide('c2p-loader');
      show('clickToPaySignIn');
      hide('guestCheckoutPanel');
      if (window.ClickToPay && window.ClickToPay.unbindCookies)
        window.ClickToPay.unbindCookies();
    };

    const fail = (msg) => {
      flags.current.settled = true;
      setErrorMsg(msg);
      setPhase('error');
      if (onError) onError(msg);
    };

    const confirmPay = async (correlationId, scheme) => {
      flags.current.settled = true;
      setPhase('paying');
      try {
        const { data } = await confirmClickToPay({
          mco,
          amount,
          sessionId: sess.current.sessionId,
          correlationId: correlationId || '',
          scheme: scheme || ''
        });
        const parsed =
          typeof data === 'string'
            ? JSON.parse(data)
            : data && data.d
            ? JSON.parse(data.d)
            : data;
        // confirm_c2p_web (ERC_MOB_V2_WS.asmx.cs:15891) has two distinct
        // response shapes, NOT a shared {status:'OK'|'FAIL'} contract:
        //  - failure (its own catch/error branches): { status: "FAIL", error }
        //  - success: it calls update_cart(mco, ""), which writes its own
        //    response — { status: true, data: {...cart...} } — a boolean,
        //    never the string "OK". So the only reliable signal is
        //    status === "FAIL" for failure; anything else (status === true,
        //    or a response with no explicit FAIL) is success. This is the
        //    only charge-confirmation step in this flow, so fail closed on
        //    anything unparseable.
        if (!parsed || parsed.status === 'FAIL') {
          fail(
            parsed?.error ||
              parsed?.error_message ||
              'Payment could not be confirmed. Please try again.'
          );
          return;
        }
        setPhase('done');
        if (onSuccess) onSuccess(mco);
      } catch (e) {
        fail('Payment failed. Please try again.');
      }
    };

    const configurePaymentSession = () => {
      const CTP = window.ClickToPay;
      const PS = window.PaymentSession;
      if (!PS || flags.current.psConfigured) return;
      flags.current.psConfigured = true;
      try {
        PS.configure({
          session: sess.current.sessionId,
          // The mco already exists (minted by the mobile app before this
          // page ever loads), so — unlike the web modal — this is the real
          // order id, not a placeholder.
          order: { id: mco },
          fields: {
            card: {
              number: '#card-number',
              securityCode: '#security-code',
              expiryMonth: '#expiry-month',
              expiryYear: '#expiry-year',
              nameOnCard: '#cardholder-name'
            }
          },
          frameEmbeddingMitigation: ['javascript'],
          callbacks: {
            initialized: (response) => {
              if (response && response.status === 'ok') {
                flags.current.cardFormReady = true;
              } else {
                const why =
                  (response && (response.message || response.status)) ||
                  'unknown';
                console.error(
                  '[C2P mobile] PaymentSession initialized NOT ok:',
                  why,
                  response
                );
                setErrorMsg('Could not load the card form: ' + why);
              }
            },
            formSessionUpdate: (response) => {
              if (response.status === 'ok' && flags.current.doEnroll) {
                flags.current.doEnroll = false;
                hide('clickToPayEmailPanel');
                hide('clickToPaySignIn');
                hide('guestCheckoutPanel');
                hide('customNotMyCards');
                hide('dvAddingCard');
                hide('openGuestCheckoutButton');
                hide('c2p-popup');
                hide('c2p-loader');
                show('cardFacilitator');
                setTimeout(() => {
                  CTP.checkoutWithNewCard({
                    onComplete: (correlationId, scheme) =>
                      confirmPay(correlationId || '', scheme || ''),
                    onError: () => showPageContent(),
                    onCancel: () => showPageContent()
                  });
                  const observer = new MutationObserver((mutations, obs) => {
                    if (
                      document.querySelector(
                        "iframe[src*='src.mastercard.com']"
                      )
                    ) {
                      hidePageContent();
                      obs.disconnect();
                    }
                  });
                  observer.observe(document.body, {
                    childList: true,
                    subtree: true
                  });
                }, 100);
              } else if (response.status === 'ok' && !flags.current.doEnroll) {
                confirmPay('', '');
              }
            },
            onCancel: () => showPageContent(),
            onError: () => showPageContent()
          },
          interaction: {
            displayControl: {
              formatCard: 'EMBOSSED',
              invalidFieldCharacters: 'REJECT'
            }
          }
        });

        PS.onCardTypeChange((selector, result) => {
          if (result.status === 'SUPPORTED') {
            CTP.isEnrollmentAvailableForScheme(result.brand, (canEnroll) => {
              if (canEnroll && result.brand === 'MASTERCARD') {
                show('consent-containerMaster');
                hide('consent-containerVisa');
              } else if (canEnroll && result.brand === 'VISA') {
                show('consent-containerVisa');
                hide('consent-containerMaster');
              } else {
                hide('consent-containerVisa');
                hide('consent-containerMaster');
              }
            });
          }
        });
      } catch (e) {
        flags.current.psConfigured = false;
        console.error('[C2P mobile] PaymentSession.configure threw', e);
        setErrorMsg('Could not load the card form. Please try again.');
      }
    };

    const configureSdk = () => {
      const CTP = window.ClickToPay;
      const PS = window.PaymentSession;
      if (!CTP || !PS) {
        fail('Click to Pay SDK failed to load');
        return;
      }

      CTP.configure({
        merchant: {
          id: sess.current.merchantId,
          name: 'Emirates Red Crescent',
          url: 'https://www.emiratesrc.ae/'
        },
        session: {
          id: sess.current.sessionId,
          wsVersion: sess.current.wsVersion
        },
        order: { amount: String(amount), currency: 'AED' },
        interaction: { locale: 'ar_AE', country: 'ARE' },
        customer: {
          email:
            (document.getElementById('c2pEmailField') &&
              document.getElementById('c2pEmailField').value) ||
            undefined
        },
        elements: {
          cardList: 'cardListContainer',
          otp: 'otpContainer',
          dcf: 'cardFacilitator'
        },
        callbacks: {
          onComplete: (correlationId, scheme) =>
            confirmPay(correlationId, scheme),
          onStateChange: (changeInfo) => {
            const ns = changeInfo && changeInfo.newState;
            if (ns && ns.elementState?.dcf?.visible === true) {
              hide('clickToPayEmailPanel');
              hide('clickToPaySignIn');
              hide('guestCheckoutPanel');
              hide('customNotMyCards');
              hide('dvAddingCard');
              hide('openGuestCheckoutButton');
              hide('c2p-popup');
              hide('c2p-loader');
              return;
            }
            if (
              ns &&
              (ns.elementState?.cardList?.visible === true ||
                ns.elementState?.otp?.visible === true ||
                ns.elementState?.dcf?.visible === true)
            ) {
              hide('c2p-loader');
            }
            if (
              ns &&
              (ns.payerState?.deviceRecognized === true ||
                ns.payerState?.emailEnrolled === true)
            ) {
              setTimeout(() => {
                hide('clickToPayEmailPanel');
                hide('clickToPaySignIn');
              }, 100);
            } else {
              show('clickToPayEmailPanel');
              if (flags.current.emailLookupInitiated) {
                hide('c2p-loader');
                show('c2p-popup');
              }
            }
            if (changeInfo?.diff?.elementState?.dcf?.visible === false) {
              if (changeInfo?.diff?.elementState?.cardList?.visible !== true)
                showPageContent();
            }
            if (changeInfo?.diff?.customerState?.emailEnrolled === false)
              showPageContent();
            const guest = document.getElementById('guestCheckoutPanel');
            if (
              ns?.elementState?.cardList?.visible === true &&
              ns?.elementState?.otp?.visible === false &&
              guest &&
              getComputedStyle(guest).display === 'none'
            ) {
              const el = document.getElementById('maskedEmail');
              if (el) el.textContent = maskEmail(ns?.customerState?.email);
              show('customNotMyCards');
              show('dvAddingCard');
            } else {
              hide('dvAddingCard');
              hide('customNotMyCards');
            }
          },
          onError: (errInfo) => {
            const msg = String(
              (errInfo && (errInfo.errorMessage || errInfo.message)) || ''
            );
            const fatal =
              /configure\(\) did not complete/i.test(msg) ||
              /populateAcceptedShipping/i.test(msg) ||
              /Cannot read properties of undefined/i.test(msg);
            if (!flags.current.settled && fatal) {
              fail(
                'Click to Pay is not available right now. Please use another payment method.'
              );
            }
          },
          onCancel: () => showPageContent()
        }
      });
    };

    const setup = async () => {
      try {
        if (!mco || !amount) {
          fail('Missing order reference or amount.');
          return;
        }
        const res = await createC2PSession();
        const start =
          typeof res.data === 'string'
            ? JSON.parse(res.data)
            : res.data && res.data.d
            ? JSON.parse(res.data.d)
            : res.data;
        if (!start || start.status !== 'OK') {
          if (start && start.error)
            console.error('[C2P mobile] start error', start.error);
          fail('Could not start Click to Pay. Please try again.');
          return;
        }
        if (cancelled) return;
        sess.current = {
          sessionId: start.sessionId,
          merchantId: start.merchantId,
          wsVersion: start.wsVersion || '100'
        };
        const hd = document.getElementById('hdSessionID');
        if (hd) hd.value = start.sessionId;
        const host = (start.gatewayHost || '').replace(/\/+$/, '');
        await loadScript(
          `${host}/form/version/${sess.current.wsVersion}/merchant/${start.merchantId}/session.js`
        );
        await loadScript(`${host}/static/click-to-pay/click-to-pay.min.js`);
        if (cancelled) return;
        configureSdk();
        setPhase('ready');
      } catch (e) {
        fail(e.message || 'Click to Pay error');
      }
    };

    window.__erc_c2p_mob = {
      lookupEmail: () => {
        const field = document.getElementById('c2pEmailField');
        const email = (field && field.value) || '';
        if (!email) return;
        hide('clickToPayEmailPanel');
        show('c2p-loader');
        flags.current.emailLookupInitiated = true;
        if (window.ClickToPay) window.ClickToPay.lookupCustomer(email);
      },
      openGuest: () => {
        hide('clickToPayEmailPanel');
        hide('c2p-loader');
        hide('c2p-popup');
        show('guestCheckoutPanel');
        hide('openGuestCheckoutButton');
        configurePaymentSession();
      },
      closeGuest: () => {
        if (flags.current.addingNewCard) {
          show('cardListContainer');
          show('dvAddingCard');
        } else {
          hide('c2p-loader');
          show('clickToPaySignIn');
        }
        hide('guestCheckoutPanel');
        show('openGuestCheckoutButton');
      },
      addNewCard: () => {
        show('guestCheckoutPanel');
        hide('openGuestCheckoutButton');
        hide('dvAddingCard');
        hide('cardListContainer');
        flags.current.addingNewCard = true;
        configurePaymentSession();
      },
      notMyCards: () => {
        hide('customNotMyCards');
        hide('dvAddingCard');
        hide('cardListContainer');
        hide('c2p-loader');
        show('clickToPaySignIn');
        show('clickToPayEmailPanel');
        const ef = document.getElementById('c2pEmailField');
        if (ef) ef.value = '';
        if (window.ClickToPay) window.ClickToPay.unbindCookies();
      },
      pay: () => {
        flags.current.doEnroll = checkC2PConsent();
        if (window.PaymentSession)
          window.PaymentSession.updateSessionFromForm('card');
        hide('guestCheckoutPanel');
        return false;
      }
    };

    setup();
    return () => {
      cancelled = true;
      delete window.__erc_c2p_mob;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="c2p-overlay">
      <div className="c2p-modal">
        {/* Tap-outside-to-dismiss doesn't fire reliably on mobile WebViews,
            so this page (unlike the desktop checkout modal) needs its own
            explicit close control. Reports back to the app the same way
            success/error do, with status=cancelled. */}
        <button
          type="button"
          className="c2p-close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>

        {phase === 'loading' && (
          <div className="c2p-status">
            Preparing Click to Pay…
            <div className="c2p-spinner" />
          </div>
        )}

        {phase === 'paying' && (
          <div className="c2p-status">
            Processing your payment…
            <div className="c2p-spinner" />
          </div>
        )}

        {phase === 'done' && (
          <div className="c2p-status c2p-thanks">
            Thank you. Your donation was completed.
            <div className="c2p-ref">Reference: {mco}</div>
          </div>
        )}

        {phase === 'error' && (
          <div className="c2p-status c2p-error">
            {errorMsg || 'Payment could not be completed. Please try again.'}
          </div>
        )}

        <div
          className="c2p-body"
          style={{ display: phase === 'ready' ? 'block' : 'none' }}
        >
          <input type="hidden" id="hdSessionID" defaultValue="" />

          <div className="dv-images">
            <src-button
              locale=""
              theme="light"
              card-brands='["mastercard","visa"]'
            ></src-button>
          </div>

          <h5 id="c2p-msg">Complete payment with Click to Pay</h5>

          <div
            id="customNotMyCards"
            className="c2p-notCard"
            style={{ display: 'none' }}
          >
            <label id="maskedEmail">a****g@mail.com</label>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                window.__erc_c2p_mob?.notMyCards();
              }}
            >
              Not your cards?
            </a>
          </div>

          <div id="cardListContainer"></div>
          <div id="otpContainer"></div>
          <div id="cardFacilitator"></div>

          <div id="dvAddingCard" style={{ display: 'none' }}>
            <div className="c2p-or">Or</div>
            <button
              id="addNewCardBtn"
              type="button"
              onClick={() => window.__erc_c2p_mob?.addNewCard()}
            >
              Add New Card
            </button>
          </div>

          <div id="c2p-loader" style={{ display: 'none' }}>
            <src-loader locale=""></src-loader>
            <div>Processing…</div>
          </div>

          <div id="c2p-popup" className="c2p-popup" style={{ display: 'none' }}>
            <div className="c2p-popup-content">
              <span>
                We could not find a Click to Pay account linked with this email.
                Proceed with manual card entry.
              </span>
              <button
                type="button"
                onClick={() => window.__erc_c2p_mob?.openGuest()}
              >
                OK
              </button>
            </div>
          </div>

          <div id="clickToPaySignIn">
            <div id="clickToPayEmailPanel">
              <label htmlFor="c2pEmailField">Email / البريد الالكتروني</label>
              <input
                id="c2pEmailField"
                type="email"
                maxLength={254}
                defaultValue=""
              />
              <button
                type="button"
                onClick={() => window.__erc_c2p_mob?.lookupEmail()}
              >
                Continue
              </button>
            </div>
          </div>

          <div id="guestCheckoutPanel" style={{ display: 'none' }}>
            <button
              type="button"
              className="c2p-close"
              onClick={() => window.__erc_c2p_mob?.closeGuest()}
            >
              ×
            </button>
            <h5>Enter the card details</h5>

            <label htmlFor="card-number">Card Number / رقم البطاقة</label>
            <input
              id="card-number"
              type="number"
              inputMode="numeric"
              dir="ltr"
              className="c2p-hosted-field"
              required
              readOnly
            />
            <label htmlFor="expiry-month">Expiry date / تاريخ الإنتهاء</label>
            <div className="c2p-expiry">
              <input
                id="expiry-month"
                type="text"
                inputMode="numeric"
                placeholder="MM"
                maxLength={2}
                dir="ltr"
                className="c2p-hosted-field"
                required
                readOnly
              />
              <input
                id="expiry-year"
                type="text"
                inputMode="numeric"
                placeholder="YY"
                maxLength={2}
                dir="ltr"
                className="c2p-hosted-field"
                required
                readOnly
              />
            </div>
            <label htmlFor="security-code">CVC / الرقم التعريفي</label>
            <input
              id="security-code"
              type="text"
              inputMode="numeric"
              className="c2p-hosted-field"
              required
              readOnly
            />
            <label htmlFor="cardholder-name">
              Cardholder Name / اسم حامل البطاقة
            </label>
            <input
              id="cardholder-name"
              type="text"
              className="c2p-hosted-field"
              required
              readOnly
            />

            <div id="consent-containerMaster" style={{ display: 'none' }}>
              <src-consent
                locale="en_US"
                card-brands="mastercard"
                display-remember-me="false"
                dcf-suppressed="false"
              ></src-consent>
            </div>
            <div id="consent-containerVisa" style={{ display: 'none' }}>
              <src-consent
                locale="en_US"
                card-brands="visa"
                display-remember-me="false"
                dcf-suppressed="false"
              ></src-consent>
            </div>

            <button
              type="button"
              className="c2p-pay-btn"
              onClick={() => window.__erc_c2p_mob?.pay()}
            >
              Pay - أدفع
            </button>
          </div>

          <a
            href="#"
            id="openGuestCheckoutButton"
            onClick={(e) => {
              e.preventDefault();
              window.__erc_c2p_mob?.openGuest();
            }}
          >
            Write card details
          </a>
        </div>
      </div>
    </div>
  );
};

export default MobileClickToPayWidget;
